import { Injectable } from '@angular/core';
import {ApplicationData} from "../models/application-data.model";
import {HttpClient} from "@angular/common/http";
import {Configuration} from "../models/configuration.model";
import {catchError, EMPTY, lastValueFrom, Observable, of, take, tap} from "rxjs";
import {environment} from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private _backendBaseUrl: string = environment.backEndUrl;

  data: ApplicationData = new ApplicationData();

  constructor(private _http: HttpClient) { }

  /**
   * Get configuration by key
   * @param key Configuration key
   * @returns Observable of Configuration
   */
  getConfiguration(key: string) : Observable<Configuration> {
    return this._http.get<Configuration>(this._backendBaseUrl + `/configurations/${key}`);
  }

  /**
   * Get all configurations
   * @returns Observable of Configuration array
   */
  getConfigurations() : Observable<Configuration[]> {
    return this._http.get<Configuration[]>(this._backendBaseUrl + `/configurations`);
  }

  /**
   * Set individual property in application data
   * @param propertyName Property name
   * @param value Property value
   */
  private setConfiguration<K extends keyof ApplicationData>(propertyName: K, value: string): void {
    if (propertyName in this.data) {
      if (typeof this.data[propertyName] === 'number') {
        this.data[propertyName] = Number(value) as ApplicationData[K];
      } else {
        this.data[propertyName] = value as ApplicationData[K];
      }
    }
  }

  /**
   * Set multiple configurations
   * @param configs Array of Configuration
   */
  private setConfigurations(configs: Configuration[]): void {
    configs.forEach(config => this.setConfiguration(config.key as keyof ApplicationData, config.value));
  }

  /**
   * Load configurations and set application data
   * @returns Promise<void>
   */
  async loadConfigurations(): Promise<void> {
    try {
      await lastValueFrom(
        this.getConfigurations().pipe(
          tap((configs: Configuration[]) => this.setConfigurations(configs)),
          catchError((error) => {
            return EMPTY;
          }),
          take(1)
        )
      );
    } catch (error) {
    }
  }
}
